import { installedItems } from "@p0security/shared/install/installed";
import {
  AzureAdIntegration,
  EntraIdIntegration,
  isLegacyAzureAdIntegration,
} from "@p0security/shared/integrations/directories/azure-ad/types";
import { OktaIntegration } from "@p0security/shared/integrations/directories/okta/types";
import { AwsIntegration } from "@p0security/shared/integrations/resources/aws/types";
import { AzureIntegration } from "@p0security/shared/integrations/resources/azure/types";
import { CopilotStudioIntegration } from "@p0security/shared/integrations/resources/copilot-studio/types";
import { GcloudIntegration } from "@p0security/shared/integrations/resources/gcloud/types";
import { IntegrationStatus } from "@p0security/shared/integrations/shared";
import { AssessmentScopeIntegration } from "@p0security/shared/types/assessment";
import { useFlags } from "launchdarkly-react-client-sdk";
import { size } from "lodash";
import { useFirestoreDoc } from "providers/FirestoreProvider";
import { useMemo } from "react";

export const useInstalledIntegrations = () => {
  const flags = useFlags();
  const { entraAssessment, oktaAssessment, customAssessment, azureAssessment } =
    flags;
  const { doc: gcloudDoc, loading: gcloudLoading } =
    useFirestoreDoc<GcloudIntegration>(`integrations/gcloud`, {
      live: true,
      tenantAware: true,
    });

  const { doc: awsDoc, loading: awsLoading } = useFirestoreDoc<AwsIntegration>(
    `integrations/aws`,
    {
      live: true,
      tenantAware: true,
    }
  );

  const { doc: oktaDoc, loading: oktaLoading } =
    useFirestoreDoc<OktaIntegration>(`integrations/okta`, {
      live: true,
      tenantAware: true,
    });

  const { doc: workspaceDoc, loading: workspaceLoading } = useFirestoreDoc<{
    state: IntegrationStatus;
  }>(`integrations/workspace`, {
    live: true,
    tenantAware: true,
  });

  const { doc: azureAdDoc, loading: azureAdLoading } =
    useFirestoreDoc<AzureAdIntegration>(`integrations/azure-ad`, {
      live: true,
      tenantAware: true,
    });

  const { doc: entraDoc, loading: entraLoading } =
    useFirestoreDoc<EntraIdIntegration>(`integrations/entra-id`, {
      live: true,
      tenantAware: true,
    });

  const { doc: azureDoc, loading: azureLoading } =
    useFirestoreDoc<AzureIntegration>(`integrations/azure`, {
      live: true,
      tenantAware: true,
    });

  const { doc: sshDoc, loading: sshLoading } = useFirestoreDoc<{
    "iam-write": Record<string, { state: IntegrationStatus }>;
  }>(`integrations/ssh`, {
    live: true,
    tenantAware: true,
  });

  const { doc: copilotStudioDoc, loading: copilotStudioLoading } =
    useFirestoreDoc<CopilotStudioIntegration>(`integrations/copilot-studio`, {
      live: true,
      tenantAware: true,
    });

  const installed: Record<AssessmentScopeIntegration, boolean> = useMemo(
    () => ({
      aws: !!size(installedItems("iam-assessment", awsDoc?.data ?? {})),
      azure: azureAssessment
        ? !!size(installedItems("iam-assessment", azureDoc?.data ?? {}))
        : false,
      custom: !!customAssessment, // Controlled by LaunchDarkly feature flag
      gcloud: !!(
        size(installedItems("iam-assessment", gcloudDoc?.data ?? {})) ||
        size(installedItems("org-iam-assessment", gcloudDoc?.data ?? {}))
      ),
      k8s: false, //TODO: Update to make this reflect accurately
      "local-agents": true,
      okta:
        oktaAssessment || true
          ? !!size(installedItems("listing", oktaDoc?.data ?? {}))
          : false,
      workspace: workspaceDoc?.data?.state === "installed",
      "azure-ad": entraAssessment
        ? azureAdDoc?.data &&
          (isLegacyAzureAdIntegration(azureAdDoc.data)
            ? azureAdDoc.data.state === "installed"
            : !!size(installedItems("iam-write", azureAdDoc.data)))
        : false,
      "entra-id": entraAssessment
        ? !!size(installedItems("iam-write", entraDoc?.data ?? {}))
        : false,
      ssh: !!size(installedItems("iam-write", sshDoc?.data ?? {})),
      "copilot-studio": !!size(
        installedItems("iam-assessment", copilotStudioDoc?.data ?? {})
      ),
    }),
    [
      awsDoc?.data,
      azureAssessment,
      azureDoc?.data,
      copilotStudioDoc?.data,
      customAssessment,
      gcloudDoc?.data,
      oktaAssessment,
      oktaDoc?.data,
      workspaceDoc?.data?.state,
      entraAssessment,
      azureAdDoc?.data,
      entraDoc?.data,
      sshDoc?.data,
    ]
  );

  const loading =
    awsLoading ||
    copilotStudioLoading ||
    gcloudLoading ||
    oktaLoading ||
    azureAdLoading ||
    azureLoading ||
    entraLoading ||
    sshLoading ||
    workspaceLoading;

  return { loading, installed };
};
